import '@adyen/adyen-web/styles/adyen.css';

import type { CoreConfiguration } from '@adyen/adyen-web';
import { AdyenCheckout, Card, Dropin } from '@adyen/adyen-web';
import { useEffect, useRef } from 'react';

export type AdyenBillingAddress = {
  street?: string;
  houseNumberOrName?: string;
  city?: string;
  stateOrProvince?: string;
  postalCode?: string;
  country?: string;
};

export type AdyenPaymentCompletedResult = {
  resultCode: string;
  sessionResult?: string;
  billingAddress?: AdyenBillingAddress;
  cardHolderName?: string;
};

type AdyenDropInProps = {
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  onPaymentCompleted: (result: AdyenPaymentCompletedResult) => void;
  onError: (error: { message: string }) => void;
};

export const AdyenDropIn = ({
  sessionId,
  sessionData,
  clientKey,
  environment,
  onPaymentCompleted,
  onError,
}: AdyenDropInProps) => {
  const dropinRef = useRef<HTMLDivElement>(null);
  const dropinInstanceRef = useRef<InstanceType<typeof Dropin> | null>(null);
  const lastSubmittedDataRef = useRef<{
    billingAddress?: AdyenBillingAddress;
    cardHolderName?: string;
  }>({});

  // Stable refs to avoid re-initializing the Adyen checkout on callback changes
  const onPaymentCompletedRef = useRef(onPaymentCompleted);
  onPaymentCompletedRef.current = onPaymentCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let mounted = true;

    const initCheckout = async () => {
      if (!dropinRef.current) return;

      try {
        // Register components before creating the checkout (required in v6)
        AdyenCheckout.register(Dropin, Card);

        const checkout = await AdyenCheckout({
          environment: environment as 'test' | 'live',
          clientKey,
          session: {
            id: sessionId,
            sessionData,
          },
          beforeSubmit: (
            data: {
              billingAddress?: AdyenBillingAddress;
              paymentMethod?: { holderName?: string };
            },
            _component: unknown,
            actions: { resolve: (data: unknown) => void }
          ) => {
            // Capture billing address and cardholder name before Adyen processes the payment
            lastSubmittedDataRef.current = {
              billingAddress: data.billingAddress,
              cardHolderName: data.paymentMethod?.holderName,
            };
            actions.resolve(data);
          },
          onPaymentCompleted: (
            result: {
              resultCode: string;
              sessionResult?: string;
            },
            _element: unknown
          ) => {
            if (mounted) {
              onPaymentCompletedRef.current({
                resultCode: result.resultCode,
                sessionResult: result.sessionResult,
                billingAddress: lastSubmittedDataRef.current.billingAddress,
                cardHolderName: lastSubmittedDataRef.current.cardHolderName,
              });
            }
          },
          onError: (error: { message?: string }) => {
            if (mounted) {
              onErrorRef.current({
                message: error.message || 'Payment error',
              });
            }
          },
        } as CoreConfiguration);

        if (mounted && dropinRef.current) {
          const dropin = new Dropin(checkout, {
            showPaymentMethods: true,
            paymentMethodComponents: [Card],
            paymentMethodsConfiguration: {
              card: {
                billingAddressRequired: true,
                hasHolderName: true,
                holderNameRequired: true,
              },
            },
          });
          dropinInstanceRef.current = dropin;
          dropin.mount(dropinRef.current);
        }
      } catch (error) {
        console.error('Adyen Drop-in init error:', error);
        if (mounted) {
          onErrorRef.current({
            message:
              error instanceof Error
                ? error.message
                : 'Failed to initialize payment form',
          });
        }
      }
    };

    initCheckout();

    return () => {
      mounted = false;
      if (dropinInstanceRef.current) {
        try {
          dropinInstanceRef.current.unmount();
        } catch {
          // Cleanup errors are non-critical
        }
      }
    };
  }, [sessionId, sessionData, clientKey, environment]);

  return <div ref={dropinRef} />;
};
